import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {ParameterStructuredTableMode} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {WorksheetSeedDataConfig} from 'notebook/worksheets/components/WorksheetAnalysis';
import {SingleAnalysisTemplateTableMapping} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigTypes';
import {
  AnalysisSourcesForConfigOrExecution,
  AnalysisSourcesOrigin,
  DatasetType,
  DocumentWithAnalysisSources,
  isLabAutoExecutionDatasetInfo,
  LabAutoConfigDatasetInfo,
  LabAutoExecutionDatasetInfo,
  PlateDatasetType,
  StepWithAnalysisSources,
  WorksheetAnalysisSources,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureAnalysisTemplateConfiguration} from 'procedures/ProcedureDomainTypes';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';

import {
  getIndividualPlateDatasetName,
  getParameterDatasetTypeFromParameterTable,
  getParameterTypeFromDatasetType,
  getPlateDesignSourceIdentifier,
  getPlateRecordSourceIdentifier,
  getPlateSourceIdentifier,
  IdBasedDatasetType,
  ParameterDatasetType,
  parsePlateDesignSourceIdentifier,
  parsePlateRecordSourceIdentifier,
  parsePlateSourceIdentifier,
  STATIC_DATASET_NAMES,
} from './procedureMethodAnalysisUtils';

const PARAMETER_DATASET_TYPES: Array<ParameterDatasetType> = [
  DatasetType.DATA_PARAMETER,
  DatasetType.MATERIAL_INPUT,
  DatasetType.MATERIAL_OUTPUT,
  DatasetType.EQUIPMENT,
];

const PARAMETER_TABLE_MODE_TO_DATASET_TYPE: Record<ParameterStructuredTableMode, DatasetType> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: DatasetType.DATA_PARAMETER,
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: DatasetType.DATA_PARAMETER,
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: DatasetType.MATERIAL_INPUT,
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: DatasetType.MATERIAL_OUTPUT,
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: DatasetType.EQUIPMENT,
};

/** @package Exported for testing */
export const STATIC_DATASET_TYPES: Array<DatasetType> = [
  DatasetType.CONDITIONS,
  DatasetType.DATA_PARAMETER,
  DatasetType.MATERIAL_INPUT,
  DatasetType.MATERIAL_OUTPUT,
  DatasetType.EQUIPMENT,
  DatasetType.TEST_ORDERS,
  DatasetType.PREVIOUS_TEST_ORDERS,
];

export function makeEmptySeedDataConfig(worksheetId: string, timestamp: string): WorksheetSeedDataConfig {
  return {
    type: SeedDataConfigType.WORKSHEET,
    data: {
      worksheetId,
      sentToAnalysisTimestamp: timestamp,
      shouldIncludeConditionsDataFrame: false,
      shouldIncludeDataParameterDataFrame: false,
      shouldIncludeMaterialInputDataFrame: false,
      shouldIncludeMaterialOutputDataFrame: false,
      shouldIncludeEquipmentDataFrame: false,
      shouldIncludeTestOrderDataFrame: false,
      shouldIncludePreviousTestOrderDataFrame: false,
      resultSchemaIds: [],
      entitySchemaIds: [],
      labAutoDatasetIds: [],
      positionsPlateIds: [],
      contentsPlateIds: [],
      plateSchemaIdsToSummarize: [],
      availablePlateIdsToSummarize: [],
      positionsPlateRecordSpecs: [],
      contentsPlateRecordSpecs: [],
      positionsPlateDesignSpecs: [],
      contentsPlateDesignSpecs: [],
    },
  };
}

export function convertConfigIntoSeedData(
  templateConfig: ProcedureAnalysisTemplateConfiguration,
  worksheetId: string,
  timestamp: string,
  // This needs to be sent to the backend within the seed data config but is not based on the template configuration
  availablePlateIdsToSummarize: Array<string>
): WorksheetSeedDataConfig {
  const seedDataConfig = makeEmptySeedDataConfig(worksheetId, timestamp);

  seedDataConfig.data.analysisTemplateConfiguration = templateConfig;
  seedDataConfig.data.availablePlateIdsToSummarize = availablePlateIdsToSummarize;
  const rawDatasetIdSetsByDatasetType: Record<IdBasedDatasetType, Set<string>> = {
    [DatasetType.ENTITY_SCHEMA]: new Set(),
    [DatasetType.RESULT_SCHEMA]: new Set(),
    [DatasetType.LAB_AUTO]: new Set(),
    [DatasetType.PLATE_SUMMARY]: new Set(),
    [DatasetType.INDIVIDUAL_PLATE_POSITIONS]: new Set(),
    [DatasetType.INDIVIDUAL_PLATE_CONTENTS]: new Set(),
    [DatasetType.PLATE_RECORD_POSITIONS]: new Set(),
    [DatasetType.PLATE_RECORD_CONTENTS]: new Set(),
    [DatasetType.PLATE_DESIGN_POSITIONS]: new Set(),
    [DatasetType.PLATE_DESIGN_CONTENTS]: new Set(),
  };

  for (const mapping of templateConfig.config.tableMappings) {
    const {datasetType, datasetId} = mapping;
    switch (datasetType) {
      case DatasetType.CONDITIONS:
        seedDataConfig.data.shouldIncludeConditionsDataFrame = true;
        break;

      case DatasetType.DATA_PARAMETER:
        seedDataConfig.data.shouldIncludeDataParameterDataFrame = true;
        break;

      case DatasetType.MATERIAL_INPUT:
        seedDataConfig.data.shouldIncludeMaterialInputDataFrame = true;
        break;

      case DatasetType.MATERIAL_OUTPUT:
        seedDataConfig.data.shouldIncludeMaterialOutputDataFrame = true;
        break;

      case DatasetType.EQUIPMENT:
        seedDataConfig.data.shouldIncludeEquipmentDataFrame = true;
        break;

      case DatasetType.TEST_ORDERS:
        seedDataConfig.data.shouldIncludeTestOrderDataFrame = true;
        break;

      case DatasetType.PREVIOUS_TEST_ORDERS:
        seedDataConfig.data.shouldIncludePreviousTestOrderDataFrame = true;
        break;

      case DatasetType.LAB_AUTO:
        assert(
          APIUtil.isDataset(datasetId),
          `Lab auto dataset IDs in seed data config should be dataset API IDs, found ${datasetId}`
        );
        rawDatasetIdSetsByDatasetType[datasetType].add(datasetId);
        break;

      case DatasetType.ENTITY_SCHEMA:
      case DatasetType.RESULT_SCHEMA:
      case DatasetType.PLATE_SUMMARY:
      case DatasetType.INDIVIDUAL_PLATE_POSITIONS:
      case DatasetType.INDIVIDUAL_PLATE_CONTENTS:
      case DatasetType.PLATE_RECORD_POSITIONS:
      case DatasetType.PLATE_RECORD_CONTENTS:
      case DatasetType.PLATE_DESIGN_POSITIONS:
      case DatasetType.PLATE_DESIGN_CONTENTS:
        rawDatasetIdSetsByDatasetType[datasetType].add(datasetId);
        break;

      default:
        assert(false, `Unexpected dataset type: ${assertNever(datasetType)}`);
    }
  }
  return {
    ...seedDataConfig,
    data: {
      ...seedDataConfig.data,
      entitySchemaIds: [...rawDatasetIdSetsByDatasetType[DatasetType.ENTITY_SCHEMA]],
      resultSchemaIds: [...rawDatasetIdSetsByDatasetType[DatasetType.RESULT_SCHEMA]],
      labAutoDatasetIds: [...rawDatasetIdSetsByDatasetType[DatasetType.LAB_AUTO]],
      plateSchemaIdsToSummarize: [...rawDatasetIdSetsByDatasetType[DatasetType.PLATE_SUMMARY]],
      positionsPlateIds: [...rawDatasetIdSetsByDatasetType[DatasetType.INDIVIDUAL_PLATE_POSITIONS]].map(
        (plateDatasetId) => parsePlateSourceIdentifier(plateDatasetId)
      ),
      contentsPlateIds: [...rawDatasetIdSetsByDatasetType[DatasetType.INDIVIDUAL_PLATE_CONTENTS]].map(
        (plateDatasetId) => parsePlateSourceIdentifier(plateDatasetId)
      ),
      positionsPlateRecordSpecs: [...rawDatasetIdSetsByDatasetType[DatasetType.PLATE_RECORD_POSITIONS]].map(
        (plateRecordDatasetId) => parsePlateRecordSourceIdentifier(plateRecordDatasetId)
      ),
      contentsPlateRecordSpecs: [...rawDatasetIdSetsByDatasetType[DatasetType.PLATE_RECORD_CONTENTS]].map(
        (plateRecordDatasetId) => parsePlateRecordSourceIdentifier(plateRecordDatasetId)
      ),
      positionsPlateDesignSpecs: [...rawDatasetIdSetsByDatasetType[DatasetType.PLATE_DESIGN_POSITIONS]].map(
        (plateDesignDatasetId) => parsePlateDesignSourceIdentifier(plateDesignDatasetId)
      ),
      contentsPlateDesignSpecs: [...rawDatasetIdSetsByDatasetType[DatasetType.PLATE_DESIGN_CONTENTS]].map(
        (plateDesignDatasetId) => parsePlateDesignSourceIdentifier(plateDesignDatasetId)
      ),
    },
  };
}

export function getDatasetNameFromId(
  datasetId: string,
  allAvailableSources: AnalysisSourcesForConfigOrExecution
): string | null {
  const staticDatasetName = STATIC_DATASET_NAMES[datasetId as DatasetType];
  if (staticDatasetName != null) {
    return staticDatasetName;
  }

  for (const step of allAvailableSources.sourcesPerStep) {
    for (const table of [...step.analysisInputs.resultTables, ...step.analysisInputs.registrationTables]) {
      if (table.schemaId === datasetId) {
        return table.schemaName;
      }
    }
    for (const plate of step.analysisInputs.plates) {
      for (const plateDatasetType of [PlateDatasetType.POSITIONS, PlateDatasetType.CONTENTS]) {
        if (getPlateSourceIdentifier(plate.plateId, plateDatasetType) === datasetId) {
          return getIndividualPlateDatasetName(plate.plateName, plateDatasetType);
        }
      }
      if (plate.plateSchemaId === datasetId) {
        return plate.plateSchemaName;
      }
    }
    for (const plateRecord of step.analysisInputs.plateRecords) {
      for (const plateDatasetType of [PlateDatasetType.POSITIONS, PlateDatasetType.CONTENTS]) {
        if (getPlateRecordSourceIdentifier(plateRecord, plateDatasetType) === datasetId) {
          return getIndividualPlateDatasetName(plateRecord.name, plateDatasetType);
        }
      }
    }
    for (const plateDesign of step.analysisInputs.plateDesigns) {
      for (const plateDatasetType of [PlateDatasetType.POSITIONS, PlateDatasetType.CONTENTS]) {
        if (getPlateDesignSourceIdentifier(plateDesign, plateDatasetType) === datasetId) {
          return getIndividualPlateDatasetName(plateDesign.name, plateDatasetType);
        }
      }
    }
    for (const run of step.analysisInputs.laRuns) {
      for (const output of run.outputs) {
        if (getLabAutoDatasetIdentifierFromOutput(output) === datasetId) {
          return isLabAutoExecutionDatasetInfo(output) ? output.datasetName : output.outputFileConfigName;
        }
      }
    }
  }
  return null;
}

export function getDatasetTypeFromId(
  datasetId: string,
  allAvailableSources: AnalysisSourcesForConfigOrExecution
): DatasetType | null {
  if (STATIC_DATASET_TYPES.includes(datasetId as DatasetType)) {
    return datasetId as DatasetType;
  }

  for (const step of allAvailableSources.sourcesPerStep) {
    if (step.analysisInputs.resultTables.find(({schemaId}) => schemaId === datasetId)) {
      return DatasetType.RESULT_SCHEMA;
    }

    if (step.analysisInputs.registrationTables.find(({schemaId}) => schemaId === datasetId)) {
      return DatasetType.ENTITY_SCHEMA;
    }

    if (step.analysisInputs.plates.find(({plateSchemaId}) => plateSchemaId === datasetId)) {
      return DatasetType.PLATE_SUMMARY;
    }

    for (const plate of step.analysisInputs.plates) {
      if (getPlateSourceIdentifier(plate.plateId, PlateDatasetType.POSITIONS) === datasetId) {
        return DatasetType.INDIVIDUAL_PLATE_POSITIONS;
      }
      if (getPlateSourceIdentifier(plate.plateId, PlateDatasetType.CONTENTS) === datasetId) {
        return DatasetType.INDIVIDUAL_PLATE_CONTENTS;
      }
    }

    for (const plateRecord of step.analysisInputs.plateRecords) {
      if (getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.POSITIONS) === datasetId) {
        return DatasetType.PLATE_RECORD_POSITIONS;
      }
      if (getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.CONTENTS) === datasetId) {
        return DatasetType.PLATE_RECORD_CONTENTS;
      }
    }

    for (const plateDesign of step.analysisInputs.plateDesigns) {
      if (getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.POSITIONS) === datasetId) {
        return DatasetType.PLATE_DESIGN_POSITIONS;
      }
      if (getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.CONTENTS) === datasetId) {
        return DatasetType.PLATE_DESIGN_CONTENTS;
      }
    }

    for (const run of step.analysisInputs.laRuns) {
      for (const output of run.outputs) {
        if (getLabAutoDatasetIdentifierFromOutput(output) === datasetId) {
          return DatasetType.LAB_AUTO;
        }
      }
    }

    for (const parameterTable of step.analysisInputs.parameterTables) {
      if (parameterTable.mode === datasetId) {
        return getParameterDatasetTypeFromParameterTable(parameterTable.mode);
      }
    }
  }
  return null;
}

function getLabAutoDatasetIdentifierFromOutput(
  output: LabAutoExecutionDatasetInfo | LabAutoConfigDatasetInfo
): string {
  // We use the dataset API ID in execution whereas we use the output file config API ID in configuration
  if (isLabAutoExecutionDatasetInfo(output)) {
    return output.datasetId;
  } else {
    return output.outputFileConfigId;
  }
}

export function getColumnDefsFromDatasetId(
  datasetId: string,
  allAvailableSources: AnalysisSourcesForConfigOrExecution
): Array<DataFrameColumnDef> | null {
  for (const step of allAvailableSources.sourcesPerStep) {
    if (step.analysisInputs.conditions && datasetId === DatasetType.CONDITIONS) {
      return step.analysisInputs.conditions.columnDefs;
    }

    if (step.analysisInputs.testOrders && datasetId === DatasetType.TEST_ORDERS) {
      return step.analysisInputs.testOrders.columnDefs;
    }

    if (step.analysisInputs.previousTestOrders && datasetId === DatasetType.PREVIOUS_TEST_ORDERS) {
      return step.analysisInputs.previousTestOrders.columnDefs;
    }

    for (const table of [...step.analysisInputs.resultTables, ...step.analysisInputs.registrationTables]) {
      if (table.schemaId === datasetId) {
        return table.columnDefs;
      }
    }
    for (const parameterTable of step.analysisInputs.parameterTables) {
      if (PARAMETER_TABLE_MODE_TO_DATASET_TYPE[parameterTable.mode] === datasetId) {
        return parameterTable.columnDefs;
      }
    }
    for (const plate of step.analysisInputs.plates) {
      if (getPlateSourceIdentifier(plate.plateId, PlateDatasetType.POSITIONS) === datasetId) {
        return plate.positionsColumnDefs;
      }
      if (getPlateSourceIdentifier(plate.plateId, PlateDatasetType.CONTENTS) === datasetId) {
        return plate.contentsColumnDefs;
      }
      if (plate.plateSchemaId === datasetId) {
        return plate.plateColumnDefs;
      }
    }
    for (const plateRecord of step.analysisInputs.plateRecords) {
      if (getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.POSITIONS) === datasetId) {
        return plateRecord.positionsColumnDefs;
      }
      if (getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.CONTENTS) === datasetId) {
        return plateRecord.contentsColumnDefs;
      }
    }
    for (const plateDesign of step.analysisInputs.plateDesigns) {
      if (getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.POSITIONS) === datasetId) {
        return plateDesign.positionsColumnDefs;
      }
      if (getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.CONTENTS) === datasetId) {
        return plateDesign.contentsColumnDefs;
      }
    }
    for (const run of step.analysisInputs.laRuns) {
      for (const output of run.outputs) {
        if (getLabAutoDatasetIdentifierFromOutput(output) === datasetId) {
          return output.columnDefs;
        }
      }
    }
  }
  return null;
}

function getLabAutoDatasetIdsForLabAutoOutputConfigApiId(
  outputFileConfigId: string,
  availableSources: WorksheetAnalysisSources
): Array<string> {
  return availableSources.sourcesPerStep.flatMap((step) =>
    step.analysisInputs.laRuns.flatMap((run) =>
      run.outputs
        .filter((output) => output.outputFileConfigId === outputFileConfigId)
        .filter((output) => isLabAutoExecutionDatasetInfo(output))
        .map((output) => output.datasetId)
    )
  );
}

/*
 * Updates the table mappings by converting lab auto config file API IDs to lab auto dataset API IDs.
 *
 * The config file API IDs are used in configuration, whereas the dataset API IDs are used in execution.
 */
export function getTableMappingsWithUpdatedLabAutoDatasetIds(
  originalTableMappings: Array<SingleAnalysisTemplateTableMapping>,
  analysisSources: WorksheetAnalysisSources
): Array<SingleAnalysisTemplateTableMapping> {
  // no-op for non-lab auto datasets and lab auto datasets that have already been updated
  const shouldMappingBeUpdated = (mapping: SingleAnalysisTemplateTableMapping): boolean =>
    mapping.datasetType === DatasetType.LAB_AUTO && !APIUtil.isDataset(mapping.datasetId);
  return originalTableMappings
    .filter(
      (tableMapping) =>
        !shouldMappingBeUpdated(tableMapping) ||
        // If there is not exactly one matching dataset, filter out the table mapping, so the user is forced to redo the mapping.
        // More than one matching dataset can happen if more than one output file is processed for the same output file config.
        getLabAutoDatasetIdsForLabAutoOutputConfigApiId(tableMapping.datasetId, analysisSources).length === 1
    )
    .map((tableMapping) =>
      shouldMappingBeUpdated(tableMapping)
        ? {
            ...tableMapping,
            // Convert output file config API ID to actual dataset API ID, which is expected in execution
            datasetId: getLabAutoDatasetIdsForLabAutoOutputConfigApiId(
              tableMapping.datasetId,
              analysisSources
            )[0],
          }
        : tableMapping
    );
}

function getAvailableDatasetIdsForStep(
  step: StepWithAnalysisSources | DocumentWithAnalysisSources,
  analysisSourcesOrigin: AnalysisSourcesOrigin
): Set<string> {
  const schemaDatasetIdsFromTables = [
    ...step.analysisInputs.resultTables,
    ...step.analysisInputs.registrationTables,
  ]
    .filter(
      (tableSource) =>
        tableSource.hasCreationContext || analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION
    )
    .map((tableSource) => tableSource.schemaId);
  const plateSchemaDatasetIds = step.analysisInputs.plates.map((plate) => plate.plateSchemaId);
  const plateDatasetIds = step.analysisInputs.plates.flatMap((plate) => [
    getPlateSourceIdentifier(plate.plateId, PlateDatasetType.POSITIONS),
    getPlateSourceIdentifier(plate.plateId, PlateDatasetType.CONTENTS),
  ]);
  const plateRecordDatasetIds = step.analysisInputs.plateRecords.flatMap((plateRecord) => [
    getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.POSITIONS),
    getPlateRecordSourceIdentifier(plateRecord, PlateDatasetType.CONTENTS),
  ]);
  const plateDesignDatasetIds = step.analysisInputs.plateDesigns.flatMap((plateDesign) => [
    getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.POSITIONS),
    getPlateDesignSourceIdentifier(plateDesign, PlateDatasetType.CONTENTS),
  ]);
  const labAutoDatasetIds = step.analysisInputs.laRuns.flatMap((run) =>
    run.outputs.map((output) => getLabAutoDatasetIdentifierFromOutput(output))
  );
  const availableDatasetIds = [
    ...schemaDatasetIdsFromTables,
    ...plateSchemaDatasetIds,
    ...plateDatasetIds,
    ...plateRecordDatasetIds,
    ...plateDesignDatasetIds,
    ...labAutoDatasetIds,
  ];
  if (step.analysisInputs.testOrders != null) {
    availableDatasetIds.push(DatasetType.TEST_ORDERS);
  }
  if (step.analysisInputs.previousTestOrders != null) {
    availableDatasetIds.push(DatasetType.PREVIOUS_TEST_ORDERS);
  }
  return new Set(availableDatasetIds);
}

/*
 * Checks whether the data for an expected dataset is actually present.
 */
export function isDataPresentForSourceDatasetId(
  datasetId: string,
  allAvailableSources: AnalysisSourcesForConfigOrExecution,
  analysisSourcesOrigin: AnalysisSourcesOrigin
): boolean {
  // This dataset type can always be generated
  if (datasetId === DatasetType.CONDITIONS) {
    return true;
  }
  if (PARAMETER_DATASET_TYPES.includes(datasetId as ParameterDatasetType)) {
    const parameterType = getParameterTypeFromDatasetType(datasetId as ParameterDatasetType);
    return allAvailableSources.availableParameterTypes.includes(parameterType);
  }
  for (const step of allAvailableSources.sourcesPerStep) {
    const availableDatasetIdsForStep = getAvailableDatasetIdsForStep(step, analysisSourcesOrigin);
    if (availableDatasetIdsForStep.has(datasetId)) {
      return true;
    }
  }
  return false;
}
